import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Pencil1Icon, ReaderIcon } from '@radix-ui/react-icons';
import {
  EBOOK_TIME_LIMIT_SECONDS,
  type EbookQuestion,
  type EbookAttempt,
  type EbookWorkspace,
} from '@wx-weiqi/contracts';
import { EbookResultFeedback } from './EbookResultFeedback';
import { BoardSurface } from './BoardSurface';
import { ebookMoveLabels } from '../services/ebook-variations';
import { boardViewportForCorner } from '../domain/homework-board-focus';
import { toggleWorkspaceFullscreen } from '../services/workspace-fullscreen';
import './ebook-answer.css';

export function EbookAnswerPage({
  book,
  question: q,
  position,
  moves,
  remaining,
  ready,
  completed,
  busy,
  preview,
  message,
  status,
  feedback,
  error,
  controls,
  history,
  hasNext,
  onPoint,
  onNext,
  onReturn,
}: {
  book: EbookWorkspace['book'];
  question: EbookQuestion;
  position: EbookAttempt['board'];
  moves: EbookAttempt['moves'];
  remaining: number;
  ready: boolean;
  completed: boolean;
  busy: boolean;
  preview: boolean;
  message: string;
  status: EbookAttempt['status'];
  feedback: string;
  error: string;
  controls: ReactNode;
  history: { ply: number; total: number; onNavigate: (ply: number) => void };
  hasNext: boolean;
  onPoint: (point: { x: number; y: number }) => void;
  onNext: () => void;
  onReturn: () => void;
}) {
  const workspace = useRef<HTMLElement>(null);
  const [fullscreen, setFullscreen] = useState(false);
  const [fullscreenError, setFullscreenError] = useState('');
  const [zoomed, setZoomed] = useState(false);
  useEffect(() => {
    const update = () => setFullscreen(document.fullscreenElement === workspace.current);
    document.addEventListener('fullscreenchange', update);
    return () => document.removeEventListener('fullscreenchange', update);
  }, []);
  useEffect(() => setZoomed(false), [q.id]);
  const labels = ebookMoveLabels(q.size, position.stones, moves);
  const { visible: v } = q;
  const corner =
    v.x === 0 ? (v.y === 0 ? 'TOP_LEFT' : 'BOTTOM_LEFT') : v.y === 0 ? 'TOP_RIGHT' : 'BOTTOM_RIGHT';
  // Zoom is presentation only. Every stone still uses its confirmed full-board coordinate.
  const canZoom =
    q.size === 19 &&
    v.columns <= 12 &&
    v.rows <= 12 &&
    (v.x === 0 || v.x + v.columns === q.size) &&
    (v.y === 0 || v.y + v.rows === q.size) &&
    position.stones.every(
      (s, i) =>
        !s ||
        (i % q.size >= v.x &&
          i % q.size < v.x + v.columns &&
          Math.floor(i / q.size) >= v.y &&
          Math.floor(i / q.size) < v.y + v.rows),
    );
  const lines = (q.goal || (q.currentPlayer === 'BLACK' ? '黑先' : '白先')).split(/\r?\n/);
  return (
    <main
      ref={workspace}
      className="board-workspace board-workspace--student student-play-workspace homework-answer-page warm-study ebook-answer-page"
    >
      <img
        className="study-flourish study-foreground-leaves"
        src="/assets/warm-study/foreground-leaves.webp"
        alt=""
        aria-hidden="true"
        draggable={false}
      />
      <img
        className="study-flourish study-window-vine"
        src="/assets/warm-study/window-vine.webp"
        alt=""
        aria-hidden="true"
        draggable={false}
      />
      <aside className="student-study-context" aria-label="题目信息">
        <header className="student-study-heading">
          <p className="eyebrow">
            我的电子书 <ReaderIcon aria-hidden="true" />
          </p>
          <div className="study-assignment-title">
            <div>
              <h1>{book.title}</h1>
              <span className="student-question-count">
                第 <strong>{book.questionIds.indexOf(q.id) + 1}</strong> / {book.questionIds.length}{' '}
                题
              </span>
            </div>
            <strong className="homework-first-player">
              <i className={q.currentPlayer === 'WHITE' ? 'is-white' : ''} aria-hidden="true" />
              {q.currentPlayer === 'WHITE' ? '白先' : '黑先'}
            </strong>
          </div>
          <progress
            aria-label="当前题目进度"
            value={book.questionIds.indexOf(q.id) + 1}
            max={book.questionIds.length}
          />
          {preview && <small>学生视角预览 · 不计学习成绩</small>}
        </header>
        <section className="student-question-requirements" aria-label="题目要求">
          <h2>
            <ReaderIcon aria-hidden="true" />
            题目要求
          </h2>
          <p className="ebook-requirements">
            {lines.map((line, i) => (
              <strong key={i} className={i === 0 ? 'ebook-requirements-first' : ''}>
                {line || '\u00a0'}
              </strong>
            ))}
          </p>
          {q.text && <p className="ebook-question-text">{q.text}</p>}
        </section>
        <div className="study-context-art" aria-hidden="true">
          <img
            className="study-flourish study-paper-lantern"
            src="/assets/warm-study/paper-lantern.webp"
            alt=""
            draggable={false}
          />
          <img
            className="study-decoration study-decoration--companions"
            src="/assets/warm-study/study-companions-brushes.webp?v=a1c72d2"
            alt=""
            draggable={false}
          />
        </div>
      </aside>
      <section
        className={zoomed && canZoom ? 'homework-answer-board is-zoomed' : 'homework-answer-board'}
      >
        <BoardSurface
          boardAppearance="wood"
          size={q.size}
          stones={position.stones}
          lastMove={null}
          stoneLabels={labels}
          viewport={zoomed && canZoom ? boardViewportForCorner(corner) : null}
          initialAnnotations={q.marks
            .filter((m) => !labels.some((l) => l.point.x === m.point.x && l.point.y === m.point.y))
            .map((m) =>
              m.kind === 'LABEL'
                ? { point: m.point, kind: 'LABEL' as const, text: m.label }
                : { point: m.point, kind: m.kind },
            )}
          isActionable={(_point, stone) =>
            ready &&
            !busy &&
            !completed &&
            history.ply === history.total &&
            remaining > 0 &&
            stone === null
          }
          onPoint={onPoint}
        />
        {completed &&
          status !== 'CONTINUE' &&
          (history.ply < history.total ? (
            <button
              className="ebook-board-result-reopen"
              onClick={() => {
                history.onNavigate(history.total);
              }}
            >
              查看本题结果
            </button>
          ) : (
            <EbookResultFeedback
              key={`${q.id}:${status}`}
              status={status}
              feedback={feedback}
              busy={busy}
              onReturn={onReturn}
            />
          ))}
      </section>
      <aside className="homework-answer-sidebar" aria-label="作答操作">
        <section
          className={`homework-countdown${remaining <= 10 && !completed ? ' is-urgent' : ''}${!ready ? ' is-status' : ''}`}
        >
          <img
            className="study-flourish study-timer-leaves"
            src="/assets/warm-study/timer-leaves.webp"
            alt=""
            aria-hidden="true"
            draggable={false}
          />
          <img
            className="study-timer-icon"
            src="/assets/warm-study/hourglass.webp"
            alt=""
            aria-hidden="true"
            draggable={false}
          />
          <small>本题剩余时间</small>
          <strong aria-label="倒计时">
            {ready
              ? `${String(Math.floor(remaining / 60)).padStart(2, '0')}:${String(remaining % 60).padStart(2, '0')}`
              : '同步计时…'}
          </strong>
          <progress
            aria-label="本题剩余时间比例"
            value={ready ? remaining : 0}
            max={EBOOK_TIME_LIMIT_SECONDS}
          />
        </section>
        <section className="student-answer-tools" aria-label="答题工具">
          <h2>
            <Pencil1Icon aria-hidden="true" />
            作答操作
          </h2>
          <div className="homework-answer-view-actions" role="group" aria-label="棋盘显示">
            <button
              className="fullscreen-action"
              onClick={() => {
                if (workspace.current)
                  void toggleWorkspaceFullscreen(workspace.current, document).then((e) =>
                    setFullscreenError(e ?? ''),
                  );
              }}
            >
              {fullscreen ? '退出全屏' : '进入全屏'}
            </button>
            <button disabled={!canZoom} onClick={() => setZoomed((z) => !z)}>
              {zoomed && canZoom ? '恢复全盘' : '棋盘放大'}
            </button>
          </div>
          <div className="homework-answer-progress" role="group" aria-label="作答进度">
            <button
              disabled={busy || !ready || history.ply === 0}
              onClick={() => history.onNavigate(history.ply - 1)}
            >
              后退
            </button>
            <span>
              {history.ply} / {history.total}
            </span>
            <button
              disabled={busy || !ready || history.ply === history.total}
              onClick={() => history.onNavigate(history.ply + 1)}
            >
              前进
            </button>
          </div>
          {history.ply < history.total && (
            <small>正在查看已走着手；前进到最新一手后继续答题。计时继续。</small>
          )}
          {!completed && (
            <p className="ebook-answer-result is-playing" role="status">
              {message}
            </p>
          )}
          {(error || fullscreenError) && (
            <p className="message message--error" role="alert">
              {error || fullscreenError}
            </p>
          )}
          <button
            className="homework-submit-button"
            disabled={busy || !completed || !hasNext}
            onClick={onNext}
          >
            下一题
          </button>
          <button className="ebook-return-button" disabled={busy || !completed} onClick={onReturn}>
            返回
          </button>
          {completed && !hasNext && <small>本轮练习已完成，可返回电子书。</small>}
          {controls}
        </section>
        <div className="study-desk-art" aria-hidden="true">
          {(['standard', 'tall', 'compact'] as const).map((variant, i) => (
            <img
              key={variant}
              className={`study-flourish study-desk-garden study-desk-garden--${variant}`}
              src={`/assets/warm-study/desk-still-life-${['medium', 'tall', 'compact'][i]}.webp?v=a1c72d2`}
              alt=""
              draggable={false}
            />
          ))}
        </div>
      </aside>
    </main>
  );
}
